/// <amd-module name="Feed/Connect/Components/ConnectToFeedView" />
import * as React from "react";
import { connect } from "react-redux";
import { DynamicModuleLoader } from "redux-dynamic-modules";

import { VssComponent } from "VSS/Platform/Layout";

import { IListItemDetails, IListRow, IListSelection, ISimpleListCell, List, ListItem, ListSelection } from "VSSUI/List";
import { SingleLayerMasterPanel, SingleLayerMasterPanelHeader } from "VSSUI/MasterDetails";
import { Spinner, SpinnerSize } from "VSSUI/Spinner";
import { css } from "VSSUI/Util";
import { ArrayItemProvider, IItemProvider } from "VSSUI/Utilities/Provider";

import * as Res from "Feed/Connect/Resources";
import { FeedHubActions, getSelectedUxFeedView } from "Feed/Core/Redux/FeedHub";
import { IUxFeedView } from "Feed/Core/UxFeedView";

import {
    ConnectToFeedActions,
    getConnectToFeedModule,
    getSelectedTool,
    getTools,
    IConnectToFeedAwareState
} from "../Redux/ConnectToFeed";
import { ITool } from "../Tool";
import { ToolIcon } from "./Common";
import { GetToolPanel } from "./GetToolPanel";
import { ToolDetails } from "./ToolDetails";

const Actions = {
    viewFeed: FeedHubActions.viewFeed,
    toolSelected: ConnectToFeedActions.toolSelected
};

interface IConnectToFeedViewOwnProps {
    feed?: IUxFeedView;
    selectedTool?: ITool;
    showGetTools: boolean;
    tools: ITool[];
}

type IConnectToFeedViewProps = IConnectToFeedViewOwnProps & typeof Actions;

type IToolListCell = ISimpleListCell & { tool?: ITool };

interface IState {
    selection?: IListSelection;
    itemProvider?: IItemProvider<IToolListCell>;
}

class UnconnectedConnectToFeedView extends React.Component<IConnectToFeedViewProps, IState> {
    constructor(props: IConnectToFeedViewProps, context?) {
        super(props, context);
        this.state = {};
    }

    public static getDerivedStateFromProps(nextProps: IConnectToFeedViewProps, prevState: IState) {
        if (nextProps.tools && nextProps.tools.length > 0 && !prevState.itemProvider) {
            const [selection, cells] = UnconnectedConnectToFeedView.getData(nextProps.tools, nextProps.selectedTool);
            return {
                selection,
                itemProvider: new ArrayItemProvider(cells)
            };
        }
        return null;
    }

    public render() {
        const { feed, selectedTool, showGetTools, toolSelected, viewFeed } = this.props;

        if (!feed || !this.state.itemProvider) {
            return <Spinner className="margin-top-16" size={SpinnerSize.large} />;
        }

        const subTitle = selectedTool ? feed.name : Res.ConnectToFeed_Text;

        return (
            <div className={css("connect-to-feed-view flex-row body-m", selectedTool && "tool-selected")}>
                <SingleLayerMasterPanel
                    className="master-panel"
                    renderHeader={() => (
                        <SingleLayerMasterPanelHeader
                            title={Res.ConnectToFeed_Text}
                            subTitle={subTitle}
                            onBackButtonClick={viewFeed}
                        />
                    )}
                    renderContent={this.renderContent}
                    showOnSmallScreens={!selectedTool}
                />
                {selectedTool && (
                    <ToolDetails
                        onBackButtonClick={() => {
                            this.props.toolSelected(undefined);
                            this.state.selection?.clear();
                        }}
                    />
                )}
                {showGetTools && <GetToolPanel />}
            </div>
        );
    }

    private renderContent = () => (
        <List
            className="tool-selector depth-4"
            itemProvider={this.state.itemProvider!}
            selection={this.state.selection!}
            width="100%"
            singleClickActivation={true}
            renderRow={this.renderRow}
            onActivate={(event, listRow: IListRow<IToolListCell>) => this.props.toolSelected(listRow.data.tool)}
            virtualize={false}
            ariaLabel={Res.PackageType}
        />
    );

    private renderRow = (
        index: number,
        item: IToolListCell,
        details: IListItemDetails<IToolListCell>,
        key?: string
    ) => (
        <ListItem
            key={key || "list-item" + index}
            index={index}
            details={details}
            className={css("list-item", !item.tool && "protocol font-weight-semibold")}
        >
            <div className="flex-row flex-center" aria-label={item.text}>
                {item.tool && <ToolIcon tool={item.tool} className="icon-large-margin" />}
                <span className={item.textClassName}>{item.text}</span>
            </div>
        </ListItem>
    );

    private static getData(tools: ITool[], selectedTool?: ITool): [IListSelection, IToolListCell[]] {
        const selectedToolId = selectedTool && selectedTool.id;

        // First split the tools by protocol
        const toolsBySchema = new Map<string, ITool[]>();
        tools.forEach(tool => {
            const toolSet = toolsBySchema.get(tool.protocolName);
            if (toolSet) {
                toolSet.push(tool);
            } else {
                toolsBySchema.set(tool.protocolName, [tool]);
            }
        });

        const cells: IToolListCell[] = [];
        const selection = new ListSelection({ multiSelect: false });

        toolsBySchema.forEach((toolsForProtocol, protocol) => {
            // One row for the protocol
            cells.push({ text: protocol });
            selection.addUnselectable(cells.length - 1); // protocol-name entries are not selectable

            // And one for each tool
            toolsForProtocol.forEach(tool => {
                cells.push({ text: tool.name, tool });
                if (tool.id === selectedToolId) {
                    selection.select(cells.length - 1);
                }
            });
        });

        return [selection, cells];
    }
}

function mapStateToProps(state: IConnectToFeedAwareState): IConnectToFeedViewOwnProps {
    return {
        feed: getSelectedUxFeedView(state),
        tools: getTools(state),
        selectedTool: getSelectedTool(state),
        showGetTools: !!state.connectToFeed.showGetTools
    };
}

const ConnectToFeedView = connect(mapStateToProps, Actions)(UnconnectedConnectToFeedView);

const DynamicConnectToFeedView = () => (
    <DynamicModuleLoader modules={getConnectToFeedModule()}>
        <ConnectToFeedView />
    </DynamicModuleLoader>
);

VssComponent.register("ms.feed.feed-connect.view", DynamicConnectToFeedView);
